import { Analytics } from '@vercel/analytics/react';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import './global.css'

import React, {useEffect} from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { Box } from '@mui/material';
import LandingPage from './pages/landing/Landing.js';
import Login from './pages/auth/Login.js';
import SignUp from './pages/auth/Signup.js';
import ForgotPassword from './pages/auth/ForgotPassword.js';
import ChangePassword from './pages/auth/ChangePassword.js';
import Verify from './pages/auth/Verify.js';
import Verification from './pages/auth/VerificationCheck.js';
import IdeSubmission from './pages/Ide/IdeSubmission.js';
import TopicsQuestions from './pages/courses/TopicsQuestions.js';
import Settings from './pages/settings/Settings.js';
import GoogleRedirect from './pages/redirect/GoogleRedirect.js';
import NotFound from './pages/error/404NotFound.js';
import './static/css/App.css';
import NavBar from './components/TopNavbar.js';
import NavBarAuth from './components/TopNavbarAuth.js'
import SideNav from './components/sideNavVer2.js';
import { UserProvider } from './context/userContext.js';
import Dashboard from './pages/dashboard/Dashboard.js';
import ResumeUpload from './pages/resume/ResumeUpload.js';
import ResumeForm from './pages/resume/ResumeForm.js';
import ResumeResults from './pages/resume/ResumeResults.js';
import IdeNew from './pages/Ide/IdeNew.js';
import CourseOverview from './pages/courses/CourseOverview.js';
import VirtualInterviewer from './pages/interviewer/VirtualInterviewer.js';
import InterViewStartPage from './pages/interviewer/InterviewStartPage.js';
import SubscriptionSuccess from './pages/payment/success.js';
import RoleSelection from './pages/interviewer/RoleSelection.js';
import PageTransitionWrapper, { NavigationProvider } from './components/PageTransitionWrapper';

const collapsedWidth = 0;
const expandedWidth = 250;

const navigationMap = {
    '/dashboard': {
      '/courses': 'up',
      '/resume/upload': 'up',
      '/interview-start-page': 'up',
      '/settings': 'up'
    },
    '/courses': {
      '/dashboard': 'down',
      '/resume/upload': 'up',
      '/interview-start-page': 'up',
      '/settings': 'up'
    },
    '/resume/upload': {
      '/dashboard': 'down',
      '/courses': 'down',
      '/interview-start-page': 'up',
      '/settings': 'up'
    },
    '/interview-start-page': {
      '/dashboard': 'down',
      '/courses': 'down',
      '/resume/upload': 'down',
      '/settings': 'up'
    },
    '/settings': {
      '/dashboard': 'down',
      '/courses': 'down',
      '/resume/upload': 'down',
      '/interview-start-page': 'down'
    }
  };

const App = () => {
    const [isCollapsed, setIsCollapsed] = React.useState(true); // Track sidebar state

    // Handle any dynamically created elements or third-party components
    useEffect(() => {
        // Set document level defaults
        document.documentElement.style.setProperty('font-family', '"Poppins"');
    }, []);
    
    return (
        <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="app-root">
        <UserProvider>
        <Router>
        <NavigationProvider navigationMap={navigationMap}>
            <Box sx={{ display: 'flex' }}>
                {/* Render SideNav only for non-auth pages and pass isCollapsed state */}
                <ConditionalSideBar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        padding: 0,
                        marginLeft: isCollapsed ? collapsedWidth : expandedWidth,
                        transition: 'margin-left 0.3s',
                        height: '100vh',
                        width: 'calc(100vw - 13%)', // Subtract sidebar width to prevent overflow
                        position: 'relative', // Important for animation positioning
                        overflow: 'hidden' // Prevents content from showing outside boundaries
                    }}
                >
                    <ConditionalNavBar />
                    <PageContent />
                </Box>
            </Box>
            </NavigationProvider>
        </Router>
    </UserProvider>
    <Analytics />
        </div>
        </ThemeProvider>
    );
}

const PageContent = () => {
    const location = useLocation();
    const authPaths = ['/', '/login', '/signup', '/forgotpassword', '/send-verification', '/verify-account', '/404NotFound'];
    
    if (authPaths.includes(location.pathname) || location.pathname.startsWith('/changepassword') ||
    location.pathname.startsWith('/verify-account') || location.pathname.startsWith('/google/redirect')) {
        return (
            <Routes>
                <Route exact path="/" Component={LandingPage} />
                <Route path="/login" Component={Login} />
                <Route path="/signup" Component={SignUp} />
                <Route path="/forgotpassword" Component={ForgotPassword} />
                <Route path="/changepassword/:token" Component={ChangePassword} />
                <Route path="/send-verification" Component={Verify} />
                <Route path="/verify-account/:token" Component={Verification} />
                <Route path="/google/redirect" Component={GoogleRedirect} />
                <Route path="/404NotFound" Component={NotFound} />
                <Route path="*" element={<Navigate to="/404NotFound" replace />} />
            </Routes>
        );
    }
    
    // Apply animations to non-auth pages
    return (
        <PageTransitionWrapper>
            <Routes location={location}>
                <Route element={<ProtectedRoute requireAuth={true} />}>
                    <Route path="/settings" Component={Settings} />
                    <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                    <Route path="/courses" Component={CourseOverview} />
                    <Route path="/courses/:idCourse" Component={TopicsQuestions} />
                    <Route path="/dashboard" Component={Dashboard} />
                    <Route path="/ideNew/:idQuestion" Component={IdeNew} />
                    <Route path="/ide/submission" Component={IdeSubmission} />
                </Route>

                <Route element={<ProtectedRoute requireAuth={true} requirePremium={true} />}>
                    <Route path="/resume/upload" Component={ResumeUpload}/>
                    <Route path="/resume/submit" Component={ResumeForm}/>
                    <Route path="/resume/form" Component={ResumeForm}/>
                    <Route path="/resume/analyse" Component={ResumeResults}/>
                    <Route path="/interview-start-page" Component={InterViewStartPage} />
                    <Route path="/interview-select-role" Component={RoleSelection} />
                    <Route path="/interview" Component={VirtualInterviewer} />
                </Route>
                <Route path="*" element={<Navigate to="/404NotFound" replace />} />
            </Routes>
        </PageTransitionWrapper>
    );
};

const ConditionalNavBar = () => {
    const location = useLocation();
    const authPaths = ['/login', '/signup', '/forgotpassword', '/send-verification', '/404NotFound'];
    const sideNavPaths = ['/dashboard', '/resume/upload', '/resume/submit', '/resume/analyse', '/settings', '/subscription-success', '/courses', '/role-select', '/interview', '/interview-start-page', '/interview-select-role'];
    
    // Check if it's an auth path or starts with /changepassword/
    const isAuthPath =
        authPaths.includes(location.pathname) ||
        location.pathname.startsWith('/changepassword') ||
        location.pathname.startsWith('/verify-account') ||
        location.pathname.startsWith('/google/redirect');

    if (location.pathname === '/') {
        return null;
    }

    /* eslint-disable no-restricted-globals */
   if (isAuthPath) {
    return <NavBarAuth />;
  } else if (sideNavPaths.includes(location.pathname)) {
    return null;
  } else if (location.pathname.match('/courses.*')) {
    return <></>;
  } else {
    return <NavBar />;
  }
};

// Update ConditionalSideBar
const ConditionalSideBar = ({ isCollapsed, setIsCollapsed }) => {
    const location = useLocation();
    const authPaths = ['/', '/login', '/signup', '/forgotpassword', '/send-verification', '/404NotFound', '/interview'];
    const isIdeNewPath = location.pathname.startsWith('/ideNew/');
    
    // Check if it's an auth path or starts with /changepassword/
    const isAuthPath = authPaths.includes(location.pathname) || 
                       location.pathname.startsWith('/changepassword') ||
                       location.pathname.startsWith('/verify-account') ||
                       location.pathname.startsWith('/google/redirect');
                       
    /* eslint-disable no-restricted-globals */
    return (isIdeNewPath || isAuthPath) ? null : 
           location.pathname.match('/ide.*') ? <></> : 
           <SideNav isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />;
};

export default App;